import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/admin/Sidebar';
import { AuthContext } from '../context/AuthContext';
import { obtenerCita } from '../api/citaService';
import './AdminLayout.css';

const ROUTE_LABELS = {
  dashboard: 'Dashboard',
  'citas-doctor': 'Citas del Doctor',
  citas: 'Citas',
  'historia-clinica': 'Historias Clinicas',
  pacientes: 'Pacientes e Historias',
  plantillas: 'Plantillas',
  configuracion: 'Configuracion',
  'test-receta': 'Test Recetas',
  'generar-receta': 'Generar Receta',
};

function buildRouteConfig(pathname) {
  if (/^\/dashboard\/citas\/[^/]+$/.test(pathname)) {
    return {
      title: 'Detalle de Cita',
      back: {
        label: 'Volver a Citas',
        to: '/dashboard/citas',
      },
      action: null,
    };
  }

  if (pathname.startsWith('/dashboard/pacientes')) {
    return {
      title: 'Pacientes e Historias Clinicas',
      action: {
        label: 'Nuevo Paciente',
        icon: 'fas fa-user-plus',
        event: 'nuevo-paciente',
      },
    };
  }

  if (pathname === '/dashboard/citas' || pathname.startsWith('/dashboard/citas/')) {
    return {
      title: 'Citas',
      action: {
        label: 'Nueva Cita',
        icon: 'fas fa-calendar-plus',
        event: 'nueva-cita',
      },
    };
  }

  if (pathname.startsWith('/dashboard/citas-doctor')) {
    return {
      title: 'Citas del Doctor',
      action: {
        label: 'Nueva Cita',
        icon: 'fas fa-calendar-plus',
        event: 'nueva-cita',
      },
    };
  }

  if (pathname.startsWith('/dashboard/plantillas')) {
    return {
      title: 'Plantillas',
      action: {
        label: 'Nueva Plantilla',
        icon: 'fas fa-notes-medical',
        event: 'nueva-plantilla',
      },
    };
  }

  if (pathname.startsWith('/dashboard/historia-clinica')) {
    if (/^\/dashboard\/historia-clinica\/[^/]+$/.test(pathname)) {
      return {
        title: 'Historia Clinica',
        back: {
          label: 'Volver a Pacientes',
          to: '/dashboard/pacientes',
        },
        action: null,
      };
    }

    return {
      title: 'Historias Clinicas',
      action: {
        label: 'Ir a Pacientes',
        icon: 'fas fa-users',
        to: '/dashboard/pacientes',
      },
    };
  }

  if (pathname.startsWith('/dashboard/generar-receta')) {
    return {
      title: 'Recetas Medicas',
      back: {
        label: 'Volver a Pacientes',
        to: '/dashboard/pacientes',
      },
      action: {
        label: 'Pacientes',
        icon: 'fas fa-users',
        to: '/dashboard/pacientes',
      },
    };
  }

  return {
    title: 'Dashboard',
    action: null,
  };
}

const AdminLayout = () => {
  const { currentUser, loading, isAuthenticated } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const [breadcrumbCitaNumero, setBreadcrumbCitaNumero] = useState('');

  const formatNumeroCita = (numero) => {
    if (typeof numero !== 'number') return null;
    return `C-${String(numero).padStart(4, '0')}`;
  };

  const esDetalleCita = useMemo(() => /^\/dashboard\/citas\/[^/]+$/.test(location.pathname), [location.pathname]);

  useEffect(() => {
    let cancelado = false;

    const resolverBreadcrumbCita = async () => {
      if (!esDetalleCita) {
        setBreadcrumbCitaNumero('');
        return;
      }

      const numeroDesdeState = location.state?.citaNumero;
      if (typeof numeroDesdeState === 'string' && numeroDesdeState.trim()) {
        setBreadcrumbCitaNumero(numeroDesdeState.trim());
        return;
      }

      const segmentos = location.pathname.split('/').filter(Boolean);
      const citaId = segmentos[2];

      if (!citaId) {
        setBreadcrumbCitaNumero('Detalle de Cita');
        return;
      }

      try {
        const response = await obtenerCita(citaId);
        const citaData = response?.data?.data || response?.data?.cita || response?.data;
        const numeroFormateado = formatNumeroCita(citaData?.numero);

        if (!cancelado) {
          setBreadcrumbCitaNumero(numeroFormateado || 'Detalle de Cita');
        }
      } catch (error) {
        if (!cancelado) {
          setBreadcrumbCitaNumero('Detalle de Cita');
        }
      }
    };

    resolverBreadcrumbCita();

    return () => {
      cancelado = true;
    };
  }, [esDetalleCita, location.pathname, location.state]);

  const routeConfig = useMemo(() => buildRouteConfig(location.pathname), [location.pathname]);

  const breadcrumb = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length === 0) {
      return 'Inicio';
    }

    const readable = segments.map((segment, index) => {
      const esSegmentoDetalleCita =
        segments.length === 3
        && segments[0] === 'dashboard'
        && segments[1] === 'citas'
        && index === 2;

      if (esSegmentoDetalleCita) {
        return breadcrumbCitaNumero || 'Detalle de Cita';
      }

      return ROUTE_LABELS[segment] || segment.replace(/-/g, ' ');
    });

    return ['Inicio', ...readable].join(' / ');
  }, [location.pathname, breadcrumbCitaNumero]);

  const routeTitle = useMemo(() => {
    if (!esDetalleCita) {
      return routeConfig.title;
    }

    const numeroCita = breadcrumbCitaNumero && breadcrumbCitaNumero !== 'Detalle de Cita'
      ? breadcrumbCitaNumero
      : '';

    return numeroCita ? `Detalle de Cita ${numeroCita}` : 'Detalle de Cita';
  }, [esDetalleCita, routeConfig.title, breadcrumbCitaNumero]);

  const handleHeaderAction = () => {
    if (!routeConfig.action) {
      return;
    }

    if (routeConfig.action.to) {
      navigate(routeConfig.action.to);
      return;
    }

    if (routeConfig.action.event) {
      window.dispatchEvent(
        new CustomEvent('dashboard-action', {
          detail: {
            action: routeConfig.action.event,
          },
        })
      );
    }
  };

  const handleHeaderBack = () => {
    if (routeConfig?.back?.to) {
      navigate(routeConfig.back.to);
      return;
    }

    navigate(-1);
  };

  // Si está cargando, mostrar indicador
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando...</p>
      </div>
    );
  }

  // Si no está autenticado, redirigir al login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Verificar si el usuario tiene permisos (DOCTOR o ADMIN)
  if (currentUser?.rol !== 'DOCTOR' && currentUser?.rol !== 'ADMIN') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Renderizar el layout admin con sidebar
  return (
    <div className={`dashboard-container ${isSidebarCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}>
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed((prev) => !prev)}
      />
      <div className="main-content">
        <header className="dashboard-topbar" role="banner">
          <div className="topbar-main">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarCollapsed((prev) => !prev)}
              aria-label={isSidebarCollapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            >
              <i className={`fas ${isSidebarCollapsed ? 'fa-bars' : 'fa-angle-left'}`}></i>
            </button>

            <div className="topbar-text">
              <h1>{routeTitle}</h1>
              <p>{breadcrumb}</p>
            </div>

            {routeConfig.back ? (
              <button type="button" className="topbar-back" onClick={handleHeaderBack}>
                <i className="fas fa-arrow-left"></i>
                {routeConfig.back.label}
              </button>
            ) : null}

            {routeConfig.action ? (
              <button type="button" className="topbar-action" onClick={handleHeaderAction}>
                <i className={routeConfig.action.icon}></i>
                {routeConfig.action.label}
              </button>
            ) : null}
          </div>
        </header>

        <div className="content-wrapper">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;